import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FlaskConical, ArrowRight, Check } from "lucide-react";
import { API, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";
import ShareBar from "../components/ShareBar";

const STAGES = ["IDEA", "SAMPLE", "TESTING", "VOTING", "PRODUCTION", "LAUNCH"];

function StageBar({ stage }) {
  const idx = STAGES.indexOf(stage);
  return (
    <div className="flex items-center gap-1 sm:gap-2 flex-wrap" data-testid="stage-bar">
      {STAGES.map((s, i) => (
        <div key={s} className="flex items-center gap-1 sm:gap-2">
          <span className={`text-[10px] sm:text-xs font-bold px-2.5 py-1.5 rounded-full ${i <= idx ? "bg-[#6A1B29] text-white" : "bg-stone-100 text-stone-400"}`}>{s}</span>
          {i < STAGES.length - 1 && <span className={`w-3 h-0.5 ${i < idx ? "bg-[#6A1B29]" : "bg-stone-200"}`} />}
        </div>
      ))}
    </div>
  );
}

function SecretLabList() {
  const [projects, setProjects] = useState([]);
  useEffect(() => { API.get("/secret-lab").then((r) => setProjects(r.data)).catch(() => {}); }, []);
  return (
    <div className="bg-[#1C1917] text-white min-h-[70vh]" data-testid="secret-lab-list">
      <Seo title="Secret Lab | Vote on What Suvikka Makes Next" description="Welcome to the Suvikka Secret Lab — where products begin. Vote on shades, styles and ideas and help decide what launches next." canonicalPath="/secret-lab" />
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-center">
        <span className="inline-flex p-3 rounded-2xl bg-white/10 text-[#F6A5BE]"><FlaskConical className="w-7 h-7" /></span>
        <h1 className="font-display text-4xl sm:text-6xl font-semibold mt-5">Welcome to the Secret Lab.</h1>
        <p className="text-stone-300 mt-4 max-w-xl mx-auto text-lg">Where Suvikka products begin. You don't just see what's next — you help choose it.</p>
      </section>
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 grid grid-cols-1 md:grid-cols-2 gap-6">
        {projects.length === 0 && <p className="text-stone-400 col-span-2 text-center">No live projects right now. Something is coming 💗</p>}
        {projects.map((p) => (
          <Link key={p.id} to={`/secret-lab/${p.slug}`} data-testid={`lab-project-${p.slug}`} className="group relative rounded-3xl overflow-hidden border border-white/10 bg-white/5 hover:border-[#6A1B29]/50 transition-all">
            <div className="aspect-[16/10] overflow-hidden">{p.image && <img src={p.image} alt={p.name} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" />}</div>
            <div className="p-6">
              {p.has_active_vote && <span className="inline-block text-[10px] font-bold uppercase tracking-wider bg-[#6A1B29] text-white px-2.5 py-1 rounded-full mb-2">🗳 Voting Now</span>}
              <h2 className="font-display text-2xl font-semibold">{p.name}</h2>
              <p className="text-sm text-stone-300 mt-1">{p.tagline}</p>
              <div className="mt-4"><StageBar stage={p.stage} /></div>
            </div>
          </Link>
        ))}
      </section>
    </div>
  );
}

function ProjectDetail({ slug }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => API.get(`/secret-lab/${slug}`).then((r) => setData(r.data)).catch(() => setData({ project: null }));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  if (!data) return <div className="max-w-4xl mx-auto px-4 py-20 text-stone-500">Loading…</div>;
  if (!data.project) return <div className="max-w-4xl mx-auto px-4 py-20 text-stone-500" data-testid="lab-not-found">Project not found.</div>;

  const p = data.project;
  const c = data.campaign;
  const voted = c?.voted_option;

  const vote = async (optId) => {
    if (!user) { nav("/login", { state: { from: `/secret-lab/${slug}` } }); return; }
    setBusy(true);
    try { await API.post(`/vote/${c.id}`, { option_id: optId }); toast.success("Your vote is in 💗"); await load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const results = c?.results;
  const winner = results?.options?.length ? [...results.options].sort((a, b) => (b.votes || 0) - (a.votes || 0))[0] : null;

  return (
    <div data-testid="lab-project-detail">
      <Seo title={`${p.name} | Suvikka Secret Lab`} description={p.tagline || p.story?.slice(0, 150)} canonicalPath={`/secret-lab/${slug}`} image={p.image} type="article" />
      <section className="relative bg-[#1C1917] text-white">
        {p.image && <img src={p.image} alt={p.name} className="absolute inset-0 w-full h-full object-cover opacity-30" />}
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <Link to="/secret-lab" className="text-xs text-stone-300 hover:text-white" data-testid="lab-back">← Secret Lab</Link>
          {c?.status === "voting" && <span className="ml-3 inline-block text-[10px] font-bold uppercase tracking-wider bg-[#6A1B29] px-2.5 py-1 rounded-full">🗳 Voting Now</span>}
          <h1 className="font-display text-4xl sm:text-6xl font-semibold mt-4">{p.name}</h1>
          <p className="text-stone-300 mt-3 max-w-2xl text-lg">{p.tagline}</p>
          <div className="mt-6"><StageBar stage={p.stage} /></div>
        </div>
      </section>

      {p.story && <section className="max-w-3xl mx-auto px-4 sm:px-6 py-12"><p className="text-stone-700 text-lg leading-relaxed">{p.story}</p></section>}

      {c && (
        <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-8" data-testid="vote-section">
          <div className="bg-white border border-stone-200 rounded-3xl p-6 sm:p-10">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-stone-900 text-center">{c.question}</h2>
            {c.status === "voting" && !voted && <p className="text-center text-stone-500 mt-2">The girls are deciding. Cast your vote 💗</p>}
            {voted && <p className="text-center text-[#6A1B29] font-semibold mt-2">Your vote is in — see how the girls are voting.</p>}
            {c.status === "closed" && winner && <p className="text-center text-[#6A1B29] font-semibold mt-2">You chose {winner.label} {winner.emoji}</p>}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
              {(results?.options || c.options).map((o) => {
                const showPct = o.percent != null;
                const isVoted = voted === o.id;
                const disabled = busy || c.status !== "voting" || !!voted;
                return (
                  <button key={o.id} onClick={() => !disabled && vote(o.id)} disabled={disabled} data-testid={`vote-option-${o.label.toLowerCase()}`}
                    className={`relative overflow-hidden rounded-2xl border-2 p-5 text-left transition-all ${isVoted ? "border-[#6A1B29] bg-[#FFF0F4]" : "border-stone-200 hover:border-[#6A1B29]"} ${disabled && !isVoted ? "cursor-default" : ""}`}>
                    {o.image && <img src={o.image} alt={o.label} className="w-full h-28 object-cover rounded-xl mb-3" />}
                    <div className="flex items-center justify-between">
                      <span className="font-display text-lg font-semibold text-stone-900">{o.emoji} {o.label}</span>
                      {isVoted && <Check className="w-5 h-5 text-[#6A1B29]" />}
                    </div>
                    {showPct && (
                      <div className="mt-3">
                        <div className="h-2 bg-stone-100 rounded-full overflow-hidden"><div className="h-full bg-[#6A1B29]" style={{ width: `${o.percent}%` }} /></div>
                        <p className="text-xs text-stone-500 mt-1">{o.percent}% · {o.votes} votes</p>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            {results?.total_votes != null && <p className="text-center text-sm text-stone-400 mt-6">{results.total_votes} girls have voted so far</p>}
            <div className="flex justify-center mt-6"><ShareBar text={`Vote for the next Suvikka shade — ${p.name} 💗`} /></div>
          </div>
        </section>
      )}

      {p.timeline?.length > 0 && (
        <section className="max-w-3xl mx-auto px-4 sm:px-6 py-12" data-testid="hype-timeline">
          <h2 className="font-display text-2xl font-semibold text-stone-900 mb-6">The Journey</h2>
          <div className="space-y-4">
            {p.timeline.map((s, i) => (
              <div key={i} className="flex gap-4">
                <div className="flex flex-col items-center"><span className="w-3 h-3 rounded-full bg-[#6A1B29]" />{i < p.timeline.length - 1 && <span className="w-0.5 flex-1 bg-stone-200 mt-1" />}</div>
                <div className="pb-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-[#6A1B29]">{s.day}</p>
                  <p className="font-display text-lg font-semibold text-stone-900">{s.title}</p>
                  <p className="text-sm text-stone-500">{s.note}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

export default function SecretLab() {
  const { slug } = useParams();
  return slug ? <ProjectDetail slug={slug} /> : <SecretLabList />;
}
